/* eslint-disable react-hooks/rules-of-hooks */

import { useState, useEffect } from 'react';
import { CircularProgress, Chip, Box, Typography, Divider, Grid, Paper, Button, useTheme, useMediaQuery } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GetBaysImageData } from 'api';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useSelector } from 'react-redux';
import { setHandlers } from './BayDetails/ImageBboxes';
import ImageDetailsDialog from './BayDetails/DetailsDialog';
import VMConfigComponent from './VMConfigComponent';

const BayDetails = () => {
  const { bayId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [bayDetails, setBayDetails] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const [vmConfigOpen, setVmConfigOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const selectedDate = useSelector((state) => state.customization.selectedDate)
    .toISOString()
    .slice(0, 10);

  // Set up handlers for ImageWithBoundingBoxes component
  useEffect(() => {
    setHandlers(handleOpenDialog, setImageLoaded);
  }, []);

  useEffect(() => {
    // Extract storeId from query parameters if available
    const queryParams = new URLSearchParams(location.search);
    const storeIdFromQuery = queryParams.get('storeId');
    if (storeIdFromQuery) {
      setStoreId(storeIdFromQuery);
    }

    const fetchBayDetails = async () => {
      setLoading(true);
      try {
        const response = await GetBaysImageData({
          dates: selectedDate,
          bay_id: bayId
        });
        if (response?.data) {
          setBayDetails(response.data);
          // If we don't have storeId from query params, try to get it from the response
          if (!storeIdFromQuery && response.data.store_id) {
            setStoreId(response.data.store_id);
          }
        } else {
          console.error('Error: No data available');
        }
      } catch (error) {
        console.error('Error fetching bay details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBayDetails();
  }, [bayId, selectedDate, location.search]);

  const handleOpenDialog = (imageData) => {
    // Filter out images with bounding boxes named "filled" as per memory requirement
    // const hasFilled = imageData.metadata[0]?.tray_det_bboxes?.some((box) => box.name === 'filled');
    // if (hasFilled) {
    //   return;
    // }

    setSelectedImage(imageData);
    setDialogOpen(true);
    setImageLoaded(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
    setImageLoaded(false);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: 'N/A' };

    try {
      const dateObj = new Date(timestamp);
      const date = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const time = dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });

      return { date, time };
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return { date: 'N/A', time: 'N/A' };
    }
  };

  const handleBackClick = () => {
    const storeIdToUse = storeId || bayDetails?.data[0]?.store_id;
    console.log('bayDetails is ', bayDetails);
    console.log('storeId is ', storeIdToUse);
    if (storeIdToUse) {
      navigate(`/main/stores/layout/${storeIdToUse}`);
    } else {
      // Fallback to main stores page if store ID is not available
      navigate('/main/stores');
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          width: '100%',
          mb: 4,
          gap: isMobile ? 2 : 0
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2
          }}
        >
          <Button
            variant="contained"
            onClick={handleBackClick}
            sx={{
              backgroundColor: '#2e7d32',
              color: 'black',
              '&:hover': {
                backgroundColor: '#1b5e20',
                color: 'white'
              },
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Back to Layout
          </Button>

          <Typography variant="h4" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem', alignSelf: 'center' }}>
            Bay Details for {bayDetails?.data[0]?.bay_id}
          </Typography>
        </Box>

        {!isMobile && (
          <Button
            variant="outlined"
            onClick={() => setVmConfigOpen(true)}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              borderColor: '#2e7d32', // MUI success.dark
              color: '#2e7d32',
              '&:hover': {
                backgroundColor: '#e8f5e9', // MUI success.light
                borderColor: '#2e7d32'
              }
            }}
          >
            Upload VM
          </Button>
        )}
      </Box>

      <VMConfigComponent open={vmConfigOpen} onClose={() => setVmConfigOpen(false)} storeId={storeId} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
          <CircularProgress />
        </Box>
      ) : bayDetails ? (
        <>
          {bayDetails?.data[0]?.captureDetails && bayDetails.data[0].captureDetails.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 'calc(100vh - 250px)'
              }}
            >
              <Grid container spacing={isMobile ? 2 : 3} sx={{ maxWidth: '1200px', justifyContent: 'center' }}>
                {bayDetails.data[0].captureDetails
                  // .filter((imageData) => {
                  //   // Filter out images with bounding boxes named "filled" as per memory requirement
                  //   return !imageData.metadata[0]?.tray_det_bboxes?.some((box) => box.name === 'filled');
                  // })
                  .map((imageData, index) => {
                    const { date, time } = formatTimestamp(imageData.metadata?.[0]?.timestamp);
                    const userDetails = imageData.userDetails || {};
                    const detectedObjects = imageData.metadata?.[0]?.tray_det_bboxes || [];
                    const format = bayDetails.data[0]?.format || '';
                    const isSevenEleven = format === 'SevenEleven';
                    console.log('isseven eleven is ', isSevenEleven);

                    let osaScore = imageData.metadata?.[0]?.fullness ?? 'N/A';
                    let Product_compliance_score = 'N/A';
                    // let vmScore = 'N/A';

                    if (isSevenEleven) {
                      const totalPop = imageData.metadata?.[0]?.total_pop || 0;
                      const matched = imageData.metadata?.[0]?.pop_matching_result?.matched || 0;
                      // const incorrect = imageData.metadata?.[0]?.pop_matching_result?.incorrect || 0;
                      // const wronglyPlaced = imageData.metadata?.[0]?.pop_matching_result?.wrongly_placed || 0;

                      osaScore = totalPop > 0 ? ((matched / totalPop) * 100).toFixed(2) : 'N/A';
                      Product_compliance_score = imageData?.metadata?.[0]?.product_score ?? 'N/A';

                      // if (totalPop > 0) {
                      //   // const vmCalculated = ((totalPop - (incorrect + wronglyPlaced)) / totalPop) * 100;
                      //   // vmScore = vmCalculated.toFixed(2);
                      // }
                    }

                    const objectCounts = {};
                    detectedObjects.forEach((box) => {
                      const category = box.name || box.type || 'Unknown';
                      objectCounts[category] = (objectCounts[category] || 0) + 1;
                    });

                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Paper
                          elevation={1}
                          sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: isMobile ? 'none' : 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                          onClick={() => handleOpenDialog(imageData)}
                        >
                          <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%' }}>
                            <img
                              src={imageData.image_url}
                              alt={`Capture ${index + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </Box>

                          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" fontWeight="bold">
                                Capture #{index + 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {date} {time}
                              </Typography>
                            </Box>

                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              {/* SEL Score */}
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  SEL Compliance Score
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    fontWeight: 'bold',
                                    color:
                                      osaScore !== 'N/A'
                                        ? parseFloat(osaScore) < 50
                                          ? '#d32f2f'
                                          : parseFloat(osaScore) < 80
                                          ? '#fb8c00'
                                          : '#28A745'
                                        : 'inherit'
                                  }}
                                >
                                  {osaScore !== 'N/A' ? `${osaScore}%` : 'N/A'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  Product Compliance Score
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    fontWeight: 'bold',
                                    color:
                                      Product_compliance_score !== 'N/A'
                                        ? parseFloat(Product_compliance_score) < 50
                                          ? '#d32f2f'
                                          : parseFloat(Product_compliance_score) < 80
                                          ? '#fb8c00'
                                          : '#28A745'
                                        : 'inherit'
                                  }}
                                >
                                  {Product_compliance_score !== 'N/A' ? `${Product_compliance_score}%` : 'N/A'}
                                </Typography>
                              </Box>

                              {/* VM Score */}
                              {/* <Box>
                                <Typography variant="body2" fontWeight="medium" textAlign="right">
                                  VM Score
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: vmScore !== 'N/A' ? (parseFloat(vmScore) > 85 ? '#2e7d32' : '#d32f2f') : 'text.secondary',
                                    textAlign: 'right'
                                  }}
                                >
                                  {vmScore !== 'N/A' ? `${vmScore}%` : 'N/A'}
                                </Typography>
                              </Box> */}
                            </Box>

                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="body2" fontWeight="medium">
                                User Info
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {userDetails.user_name || 'N/A'} ({userDetails.number || 'N/A'})
                              </Typography>
                            </Box>

                            <Box sx={{ mt: 'auto', backgroundColor: '#e8f5e9', p: 1.5, borderRadius: 2 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1, color: '#1b5e20' }}>
                                Detected Objects
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {Object.keys(objectCounts).length > 0 ? (
                                  Object.entries(objectCounts)
                                    .filter(([category]) => category !== 'filled') // Filter out 'filled' objects as per memory requirement
                                    .map(([category, count]) => (
                                      <Chip
                                        key={category}
                                        label={`${category} (${count})`}
                                        size="small"
                                        sx={{
                                          backgroundColor: '#2e7d32',
                                          color: 'white',
                                          borderRadius: 1,
                                          fontSize: '0.7rem',
                                          height: 'auto',
                                          '& .MuiChip-label': { py: 0.2, px: 0.8 }
                                        }}
                                      />
                                    ))
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    None detected
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
                width: '100%',
                backgroundColor: '#f5f5f5',
                borderRadius: 3,
                border: '1px dashed #ccc',
                mt: 2,
                mb: 4,
                p: 3
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#757575',
                  fontWeight: 500,
                  textAlign: 'center',
                  fontStyle: 'italic',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  fontSize: isMobile ? '1.2rem' : '1.5rem'
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#e0e0e0',
                    mb: 1
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: '30px',
                      color: '#9e9e9e'
                    }}
                  >
                    📷
                  </Box>
                </Box>
                No capture details available
                <Typography variant="body1" sx={{ color: '#9e9e9e', mt: 1, maxWidth: '500px' }}>
                  There are no images or captures for this bay. Please check back later or try a different bay.
                </Typography>
              </Typography>
            </Box>
          )}

          <ImageDetailsDialog
            dialogOpen={dialogOpen}
            handleCloseDialog={handleCloseDialog}
            selectedImage={selectedImage}
            imageLoaded={imageLoaded}
            setImageLoaded={setImageLoaded}
            formatTimestamp={formatTimestamp}
            bayDetails={bayDetails}
          />
        </>
      ) : (
        <Typography variant="h6" color="text.secondary">
          No details available for this bay.
        </Typography>
      )}
    </Box>
  );
};

export default BayDetails;
